import _ from 'lodash'

import { clearWidgetState } from '@/helpers/botWidgetStorage'
import { mergeReceived } from '@/helpers/partialObjects'

import { createBottleBotState } from './modules/bottleBot.state'
import { createInitialState } from './state'

const objectRefreshed = {} // not in Vuex state for perf, we don't need it to be reactive

// Vuex nests registered module state onto the root state; RESET_STATE must
// reset those slots in place, never delete them (deleting crashes every
// computed reading them after login/logout).
const MODULE_STATE_RESETTERS = { bottleBot: createBottleBotState, ecommerceBot: createBottleBotState }

export default {
  objectRefreshed,
  RESET_STATE(state) {
    localStorage.removeItem('franchiseMerchantId') // a merchant selection never outlives a session
    const fresh = createInitialState()
    for (const key of Object.keys(state)) if (!(key in fresh) && !(key in MODULE_STATE_RESETTERS)) delete state[key]
    for (const key of Object.keys(fresh)) state[key] = fresh[key]
    for (const [key, resetter] of Object.entries(MODULE_STATE_RESETTERS)) {
      if (state[key]) Object.assign(state[key], resetter())
      clearWidgetState(key)
    }
    for (const type of Object.keys(objectRefreshed)) delete objectRefreshed[type]
  },
  RECEIVE_OBJECT(state, object) {
    function set(obj) {
      state.objects[obj.type] = state.objects[obj.type] || {}
      state.objects[obj.type][obj.id] = mergeReceived(state.objects[obj.type][obj.id], obj)
      objectRefreshed[obj.type] = objectRefreshed[obj.type] || {}
      objectRefreshed[obj.type][obj.id] = Date.now()
    }

    if (object.data) {
      if (Array.isArray(object.data)) {
        object.data.forEach(x => set(x))
      } else {
        set(object.data)
      }
    }
    if (object.included) {
      object.included.forEach(x => set(x))
    }
  },
  DESTROY_OBJECT(state, object) {
    const obj = object.data
    delete state.objects[obj.type][obj.id]
  },
  DESTROY_OBJECT_WITHOUT_ID(state, type) {
    state.objects[type] = {}
  },
  INC_SAVING(state) {
    state.savingCount++
  },
  DEC_SAVING(state) {
    state.savingCount--
  },
  TOGGLE_OMNI(state) {
    state.omniSearchOpen = !state.omniSearchOpen
  },
  SET_LOADING_STATE(state, resourceName) {
    if (resourceName) {
      state.status[resourceName].loading = true
    }
  },
  SET_LOADED_STATE(state, resourceName) {
    state.status[resourceName].loading = false
  },
  SET_ONGOING_CALL(state, call) {
    state.ongoingCall = call
  },
  SET_LOGGED_IN_USER(state, user) {
    state.loggedInUser = user.data
  },
  SET_HIDE_NUMBERS(state, hideNumbers) {
    state.hideNumbers = hideNumbers
  },
  GET_USER(state, user) {
    if (user.data) {
      state.user = user.data.attributes
    }
  },

  SET_AUTH_TOKEN(state, token) {
    state.authToken = token
  },
  SET_PUSHER(state, pusher) {
    state.pusher = pusher
  },
  ADD_PENDING_MESSAGE(state, message) {
    state.pendingMessages[message.key] = { body: message.body, src: message.src }
  },
  REMOVE_PENDING_MESSAGE(state, key) {
    delete state.pendingMessages[key]
  },
  SIGNUP_PROPERTY_VALUES(state, object) {
    if (!object) {
      localStorage.removeItem('signupPropertyValues')
      return
    }
    const signupPropertyValues = JSON.parse(localStorage.getItem('signupPropertyValues')) || { nextPage: 1 }
    const currentNextPage = signupPropertyValues['nextPage'] || 1
    const objectNextPage = object['nextPage']
    Object.keys(object).forEach(key => {
      signupPropertyValues[key] = object[key]
    })
    if (objectNextPage > currentNextPage) {
      signupPropertyValues['nextPage'] = objectNextPage
    } else {
      signupPropertyValues['nextPage'] = currentNextPage
    }
    localStorage.setItem('signupPropertyValues', JSON.stringify(signupPropertyValues))
  },
  SET_CONVERSATION_SORT_BY(state, value) {
    state.conversationSortBy = value
    localStorage.setItem('conversationSortBy', value)
  },
  SET_CONVERSATION_SORT_DIRECTION(state, value) {
    state.conversationSortDirection = value
    localStorage.setItem('conversationSortDirection', value)
  },
  SET_ACTIVE_PLATFORM(state, platform) {
    state.activePlatformPreference = platform
    localStorage.setItem('activePlatform', platform)
  },
  SET_FRANCHISE(state, { franchise, merchants, blueprint = null }) {
    state.franchise = franchise
    state.franchiseMerchants = merchants
    state.franchiseBlueprint = blueprint
  },
  // Takes a franchise/menu status payload; the profile only carries the flag and the last sync.
  SET_FRANCHISE_MENU_STATUS(state, status) {
    if (!state.franchise) return
    const { enabled, enabled_at, started_at, finished_at, summary } = status
    state.franchise = {
      ...state.franchise,
      attributes: {
        ...state.franchise.attributes,
        menu_propagation_enabled: enabled,
        menu_propagation_enabled_at: enabled_at,
        menu_sync: { started_at, finished_at, summary }
      }
    }
  },
  SET_ACTIVE_FRANCHISE_MERCHANT(state, id) {
    state.activeFranchiseMerchantId = id
    if (id) localStorage.setItem('franchiseMerchantId', id)
    else localStorage.removeItem('franchiseMerchantId')
  },
  RESET_OBJECTS(state) {
    state.objects = {}
    for (const type of Object.keys(objectRefreshed)) delete objectRefreshed[type]
  }
}
